import React from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';

import { adaptFacetToUIFacet } from 'src/adapters/storeToUI/adaptFacetToUIFacet';
import { adaptSortOptionsToUISortOptions } from 'src/adapters/storeToUI/adaptSortOptionsToUISortOptions';
import { ListingsControls } from 'src/components/organisms/ListingsControls/ListingsControls';
import type {
  ChangedFacetDetails,
  ChangedSortDetails,
} from 'src/components/organisms/ListingsControlsOptions/types';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { updatePageURLWithQuery } from 'src/redux/products/thunks/updatePageURLWithQuery';
import { TriggerName } from 'src/redux/products/types';
import { actions } from 'src/redux/productsQuery/slice';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';

export const ListingsControlsContainer = () => {
  const dispatch = useDispatch();
  const state = useSelector((state: ReduxApplicationState) => state);
  const facets = state.productsQuery.facets;
  const sortOptions = state.productsQuery.sortOptions;

  const onAllFacetsVisible = () => {
    sendGAEvent({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: 'filter impression',
      event_label: 'all filters viewed',
    });
  };

  const onSortOptionChange = ({ activeValue }: ChangedSortDetails) => {
    batch(() => {
      dispatch(actions.changeSortOption(activeValue));
      dispatch(requestProducts({ triggerName: TriggerName.CHANGE_SORT }));
      dispatch(updatePageURLWithQuery());

      scrollToTopOfProductsList();
    });
  };

  const onFacetOptionChange = ({ ...changedFacet }: ChangedFacetDetails) => {
    const changedOption = changedFacet.options[0];

    batch(() => {
      dispatch(actions.changeFacetOption(changedFacet));
      dispatch(
        requestProducts({
          triggerName: TriggerName.CHANGE_FACETS,
          metadata: {
            facetTitle: changedFacet.title,
            optionTitle: changedOption.title!,
            optionActive: changedOption.active,
          },
        }),
      );
      dispatch(updatePageURLWithQuery());

      scrollToTopOfProductsList();
    });
  };

  return (
    <ListingsControls
      facets={adaptFacetToUIFacet(facets)}
      onAllFacetsVisible={onAllFacetsVisible}
      onFacetOptionChange={onFacetOptionChange}
      onSortOptionChange={onSortOptionChange}
      sortOptions={adaptSortOptionsToUISortOptions(sortOptions)}
    />
  );
};
